import React, { useState,useRef } from 'react';
import { ArrowLeft, Key, Shield, Lock,AlertTriangle } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const KMSPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { kmsData, region, accountName, duration } = location.state || {};
    
      const LongTextCell = ({ text, maxLength = 30 }) => {
        const [showTooltip, setShowTooltip] = useState(false);
        const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
        const tooltipRef = useRef(null);
        const cellRef = useRef(null);
        
        if (!text || text.length <= maxLength) {
          return <span>{text}</span>;
        }
        
        const handleMouseEnter = (e) => {
          setShowTooltip(true);
          setTimeout(() => {
            if (cellRef.current && tooltipRef.current) {
              const cellRect = cellRef.current.getBoundingClientRect();
              const tooltipRect = tooltipRef.current.getBoundingClientRect();
              const viewportHeight = window.innerHeight;
              const spaceBelow = viewportHeight - cellRect.bottom;
              const tooltipHeight = tooltipRect.height;
              
              if (spaceBelow < tooltipHeight) {
                setTooltipPosition({
                  top: -tooltipHeight - 5,
                  left: 0
                });
              } else {
                setTooltipPosition({
                  top: "100%",
                  left: 0
                });
              }
            }
          }, 0);
        };
      
        return (
          <div className="relative inline-block">
            <div
              ref={cellRef}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={() => setShowTooltip(false)}
              className="cursor-pointer border-b border-dotted border-gray-400"
            >
              {text.substring(0, maxLength)}...
            </div>
            {showTooltip && (
              <div 
                ref={tooltipRef}
                className="absolute z-50 w-64 p-2 text-sm bg-gray-900 text-white rounded shadow-lg"
                style={{
                  top: tooltipPosition.top,
                  left: tooltipPosition.left,
                  maxWidth: "250px",
                  wordBreak: "break-word"
                }}
              >
                {text}
              </div>
            )}
          </div>
        );
      };

  // Calculate summary statistics
  const calculateSummary = (data) => {
    if (!data) return {};
    
    const summary = {
      totalKeys: new Set(),
      keyStates: new Set(),
      keyUsages: new Set(),
      uniqueUsers: new Set()
    };

    data.forEach(event => {
      if (event.key_id) summary.totalKeys.add(event.key_id);
      if (event.key_state) summary.keyStates.add(event.key_state);
      if (event.key_usage) summary.keyUsages.add(event.key_usage);
      if (event.sourceipaddress) summary.uniqueUsers.add(event.sourceipaddress);
    });

    return {
      ...summary,
      totalKeys: summary.totalKeys.size,
      keyStates: Array.from(summary.keyStates),
      keyUsages: Array.from(summary.keyUsages),
      uniqueUsers: summary.uniqueUsers.size
    };
  };

  const summary = calculateSummary(kmsData?.data);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-[#003366]">KMS Activity Details</h1>
        </div>
        <div className="text-sm text-gray-500">
          Last {duration}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold text-[#003366] mb-4">Activity Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Total Keys</div>
            <div className="text-xl font-semibold text-[#003366]">
              {summary.totalKeys || 0}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Region</div>
            <div className="text-xl font-semibold text-[#003366]">
              {region}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Account</div>
            <div className="text-xl font-semibold text-[#003366]">
              {accountName}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Unique Users</div>
            <div className="text-xl font-semibold text-[#003366]">
              {summary.uniqueUsers || 0}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Key className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">Key Operations</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Created</span>
              <span className="font-semibold">{kmsData?.summary?.createKeyCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Scheduled for Deletion</span>
              <span className="font-semibold">{kmsData?.summary?.scheduleKeyDeletionCount || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">Key Status</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Enabled</span>
              <span className="font-semibold">{kmsData?.summary?.enableKeyCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Disabled</span>
              <span className="font-semibold">{kmsData?.summary?.disableKeyCount || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Lock className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">Key Usage</h3>
          </div>
          <div className="space-y-2">
            {summary.keyUsages?.map((usage, index) => (
              <div key={index} className="flex justify-between items-center">
                <span className="text-gray-600">{usage}</span>
                <span className="font-semibold">Active</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-[#003366] mb-4">KMS Events</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Key ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Key State
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Key Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source IP
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action Count
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {kmsData?.data && kmsData.data.length > 0 ? (kmsData?.data?.map((event, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <LongTextCell text={event.eventname} />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <LongTextCell text={event.key_id} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {event.key_state}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <LongTextCell text={event.key_usage} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {event.sourceipaddress}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {event.action_count}
                    </td>
                  </tr>
                ))):<tr>

                <td colSpan="7" className="px-6 py-10 text-center text-gray-500">
          
                  <div className="flex flex-col items-center justify-center">
          
                    <AlertTriangle className="h-10 w-10 text-gray-400 mb-2" />
          
                    <p className="text-lg font-medium">No data to display</p>
          
                    <p className="text-sm">No kms events were found for the selected time period and filters.</p>
          
                  </div>
          
                </td>
          
              </tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KMSPage;